import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Target,
  TrendingUp,
  Search,
  Settings,
  Zap,
  CheckCircle2,
  CircleDollarSign,
  Route,
  Users,
  ClipboardCheck,
  ChevronRight,
  ShieldCheck,
  LineChart,
  Workflow,
  AlertCircle,
  Building2,
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ROUTE_PATHS } from '@/lib/index';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const dataPoints = [
  {
    icon: CircleDollarSign,
    title: 'Umsatz & Deal-Größe',
    description:
      'Wir erheben Umsatz, durchschnittliche Deal-Größe und Vertriebsvolumen, um Potenziale, Engpässe und Umsatzverluste belastbar sichtbar zu machen.',
  },
  {
    icon: Route,
    title: 'Sales Cycle & Funnel-Stufen',
    description:
      'Wir analysieren, wie lange Deals wirklich brauchen und an welchen Stellen Leads, Discovery Calls, Präsentationen oder Abschlüsse verloren gehen.',
  },
  {
    icon: TrendingUp,
    title: 'Conversion Rates',
    description:
      'Conversion Rates zeigen uns nicht nur die Performance, sondern vor allem, an welcher Stelle Ihr Funnel strukturell bremst.',
  },
  {
    icon: Workflow,
    title: 'Follow-up & Reaktionszeit',
    description:
      'Schwaches Nachfassen und langsame Reaktion kosten überproportional Umsatz. Genau dort liegen oft die schnellsten Hebel.',
  },
  {
    icon: BarChart3,
    title: 'Pipeline & Transparenz',
    description:
      'Wir prüfen, ob Sie Ihren Vertrieb wirklich steuern können oder ob Chancen, Forecasts und Prioritäten im Blindflug laufen.',
  },
  {
    icon: Users,
    title: 'Akquise-Mix & Team-Struktur',
    description:
      'Wir schauen auf Kanäle, Verantwortlichkeiten und Verteilung im Team, um Abhängigkeiten und Ineffizienzen sichtbar zu machen.',
  },
];

const insights = [
  'wo Ihr Vertrieb aktuell konkret Umsatz verliert',
  'welche Engpässe Ihren Funnel messbar bremsen',
  'welche 3 Hebel den größten finanziellen Impact haben',
  'welche Maßnahmen kurzfristig und welche strukturell notwendig sind',
];

const symptoms = [
  { icon: AlertCircle, text: 'Viele Leads, aber wenig Abschlüsse' },
  { icon: AlertCircle, text: 'Langer Sales Cycle ohne klare Ursache' },
  { icon: AlertCircle, text: 'Schwache oder fehlende Nachverfolgung' },
  { icon: AlertCircle, text: 'Unklare Pipeline, kein belastbarer Forecast' },
  { icon: AlertCircle, text: 'Entscheidungen auf Bauchgefühl statt Daten' },
  { icon: AlertCircle, text: 'Hohe Performance-Varianz im Team' },
];

const targetGroups = [
  {
    icon: Building2,
    title: 'B2B-Unternehmen mit bestehendem Vertrieb',
    description: 'Sie haben bereits ein Team und Prozesse — aber das Potenzial wird nicht voll ausgeschöpft.',
  },
  {
    icon: TrendingUp,
    title: 'Teams mit Umsatz, aber fehlender Struktur',
    description: 'Es läuft, aber es lässt sich nicht sauber steuern, skalieren oder erklären.',
  },
  {
    icon: Target,
    title: 'Unternehmen, die skalieren wollen',
    description: 'Wachstum ist das Ziel — aber Engpässe im Funnel bremsen die nächste Stufe.',
  },
];

const journeySteps = [
  {
    step: '01',
    title: 'Sales Audit',
    description:
      'Ihr Unternehmen durchläuft den Audit und liefert die relevanten Sales-Daten als Grundlage für eine belastbare Erstdiagnose.',
    icon: ClipboardCheck,
  },
  {
    step: '02',
    title: 'Ergebnis & Handlungsempfehlung',
    description:
      'Sie erhalten eine priorisierte Auswertung mit Umsatzverlust, Hebeln, Quick Wins und ersten Handlungsempfehlungen.',
    icon: LineChart,
  },
  {
    step: '03',
    title: 'Deep-Dive Analyse',
    description:
      'Im nächsten Schritt prüfen wir Prozesse, Funnel-Logik, Teamstruktur, Verantwortlichkeiten und reale Bottlenecks im Detail.',
    icon: Search,
  },
  {
    step: '04',
    title: 'Sales Engine Blueprint',
    description:
      'Aus den Daten entsteht ein klarer Umsetzungsplan: Funnel, Rollen, Conversion-Hebel, Prioritäten und operative Struktur.',
    icon: Target,
  },
  {
    step: '05',
    title: 'Umsetzung & Optimierung',
    description:
      'Gemeinsam verbessern wir die kritischen Stellschrauben und bauen einen Vertrieb, der planbarer, klarer und skalierbarer arbeitet.',
    icon: Settings,
  },
];

const engineModules = [
  'Funnel-Analyse und Conversion-Optimierung',
  'Klare Sales-Prozesse und Deal-Phasen',
  'Follow-up- und Reaktionslogik',
  'Pipeline-Transparenz und Steuerbarkeit',
  'Akquise-Strategie und Kanalbewertung',
  'Rollen, Verantwortlichkeiten und Team-Fokus',
  'Priorisierte Umsatzhebel nach Impact',
  'Konkrete 30-, 60- und 90-Tage-Roadmap',
];

export default function SalesEngine() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Layout>
      <div className="flex-1">

        {/* ── HERO ── */}
        <section className="relative overflow-hidden bg-primary text-primary-foreground py-24 md:py-32">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-foreground/10 via-transparent to-transparent" />
            <div className="absolute top-0 left-0 w-96 h-96 bg-primary-foreground/10 rounded-full blur-3xl animate-pulse" />
            <div
              className="absolute bottom-0 right-0 w-96 h-96 bg-accent/15 rounded-full blur-3xl animate-pulse"
              style={{ animationDelay: '1.2s' }}
            />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="max-w-5xl mx-auto text-center"
            >
              <motion.div variants={fadeInUp} className="mb-6">
                <Badge
                  variant="outline"
                  className="text-sm px-5 py-2.5 bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Vom Audit zur skalierbaren Sales Engine
                </Badge>
              </motion.div>

              <motion.h1
                variants={fadeInUp}
                className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.08] mb-6"
              >
                Wir machen aus Ihren Daten
                <br />
                <span className="text-accent">einen Vertriebsplan mit Hebelwirkung</span>
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-xl md:text-2xl text-primary-foreground/80 max-w-3xl mx-auto leading-relaxed mb-10"
              >
                Unser Audit zeigt nicht nur, wo Ihr Vertrieb Umsatz verliert. Er schafft die Grundlage,
                daraus eine klare Sales Engine zu entwickeln — mit Prioritäten, Struktur und messbaren
                nächsten Schritten.
              </motion.p>

              <motion.div
                variants={fadeInUp}
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
              >
                <Button
                  asChild
                  size="lg"
                  className="text-lg px-8 py-6 h-auto bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                >
                  <Link to={ROUTE_PATHS.AUDIT}>
                    Audit starten
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 h-auto border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
                >
                  <Link to={ROUTE_PATHS.KONTAKT}>Gespräch anfragen</Link>
                </Button>
              </motion.div>

              {/* Prozess-Logik-Pill */}
              <motion.div variants={fadeInUp} className="mt-12">
                <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-2xl bg-primary-foreground/10 border border-primary-foreground/20 px-6 py-3 text-sm font-medium text-primary-foreground/90">
                  <span>Analyse</span>
                  <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                  <span>Priorisierung</span>
                  <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                  <span>Umsetzung</span>
                  <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                  <span className="text-accent font-semibold">Umsatzwirkung</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── TYPISCHE SYMPTOME ── */}
        <section className="py-16 border-b border-border/40">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="max-w-4xl mx-auto"
            >
              <p className="text-center text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-8">
                Kommt Ihnen das bekannt vor?
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {symptoms.map((s, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="flex items-center gap-3 rounded-xl bg-destructive/5 border border-destructive/15 px-4 py-3"
                  >
                    <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
                    <span className="text-sm font-medium text-foreground">{s.text}</span>
                  </motion.div>
                ))}
              </div>
              <p className="text-center text-sm text-muted-foreground mt-6">
                Wenn eines davon zutrifft, liefert der Audit innerhalb von 20 Minuten eine datenbasierte Diagnose.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── WARUM WIR DIESE DATEN ERHEBEN ── */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center mb-14"
            >
              <Badge className="mb-4">Datenerhebung mit Absicht</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Warum wir diese Daten erheben
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Weil Vertrieb nicht an Einzelmeinungen scheitert, sondern an unsichtbaren Engpässen,
                fehlender Steuerbarkeit und verlorenen Chancen im Funnel. Jede Frage hat einen
                konkreten analytischen Zweck.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {dataPoints.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                >
                  <Card className="h-full border-border/60 hover:shadow-lg transition-shadow duration-200">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                        <item.icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WAS WIR DARAUS ERKENNEN ── */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Badge className="mb-4">Daten werden zu Prioritäten</Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-5">
                  Was wir aus den Daten konkret erkennen
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                  Die Stärke unseres Ansatzes liegt nicht in der Datenerhebung allein, sondern in der
                  Übersetzung in klare Entscheidungen. Aus Ihren Eingaben machen wir eine verwertbare
                  Diagnose — keine Tabelle, sondern Prioritäten.
                </p>
                <div className="space-y-4">
                  {insights.map((point) => (
                    <div key={point} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <p className="text-base md:text-lg">{point}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="border-primary/15 shadow-xl">
                  <CardContent className="p-8">
                    <div className="mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                        <Zap className="w-7 h-7 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">Die Logik dahinter</h3>
                      <p className="text-muted-foreground leading-relaxed text-sm">
                        Ein schwacher Vertrieb scheitert selten an einer einzigen Sache. Meist sind es
                        mehrere kleine Brüche: zu wenig qualifizierte Kontakte, schwache Conversion,
                        fehlendes Follow-up, unklare Phasen, langsame Reaktion oder mangelnde Steuerung.
                        Wir machen genau diese Brüche sichtbar und beziffern sie monetär.
                      </p>
                    </div>
                    <div className="rounded-2xl bg-muted/50 border border-border p-5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                        Vom Input zur Entscheidung
                      </p>
                      <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                        <span className="rounded-lg bg-background border border-border px-3 py-1.5">Daten</span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        <span className="rounded-lg bg-background border border-border px-3 py-1.5">Diagnose</span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        <span className="rounded-lg bg-background border border-border px-3 py-1.5">Priorisierung</span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        <span className="rounded-lg bg-primary/10 border border-primary/20 px-3 py-1.5 text-primary font-semibold">Umsatzwirkung</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── DER WEG NACH DER HANDLUNGSEMPFEHLUNG ── */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center mb-14"
            >
              <Badge className="mb-4">Der Prozess</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                So geht es nach der Handlungsempfehlung weiter
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground">
                Der Audit ist der Einstieg — nicht das Endprodukt. Die eigentliche Wirkung entsteht,
                wenn aus den Erkenntnissen eine klare Sales Engine aufgebaut wird.
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto space-y-4">
              {journeySteps.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                >
                  <Card className="border-border/60 hover:shadow-md transition-shadow">
                    <CardContent className="p-6 md:p-7">
                      <div className="grid md:grid-cols-[80px_1fr] gap-5 items-start">
                        <div className="flex md:flex-col items-center md:items-start gap-3">
                          <div className="text-2xl font-bold text-primary/50 tabular-nums">
                            {item.step}
                          </div>
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <item.icon className="w-5 h-5 text-primary" />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-lg md:text-xl font-semibold mb-1.5">{item.title}</h3>
                          <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WAS DIE SALES ENGINE ENTHÄLT ── */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Badge className="mb-4">Was konkret entsteht</Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-5">
                  Was eine Sales Engine bei uns konkret bedeutet
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                  Wir liefern keine lose Sammlung von Tipps. Wir strukturieren die entscheidenden
                  Bereiche Ihres Vertriebs so, dass daraus ein belastbares System mit Fokus auf
                  Umsatzwirkung entsteht.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {engineModules.map((module) => (
                    <div
                      key={module}
                      className="rounded-xl border border-border bg-card px-4 py-3.5 flex items-start gap-3"
                    >
                      <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-sm">{module}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="border-primary/15 shadow-xl bg-primary text-primary-foreground">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-primary-foreground/10 flex items-center justify-center mb-5">
                      <Target className="w-7 h-7 text-primary-foreground" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold mb-4 leading-snug">
                      Das Ziel ist nicht mehr Aktivität.<br />Das Ziel ist mehr Wirkung.
                    </h3>
                    <p className="text-primary-foreground/80 leading-relaxed mb-7 text-sm md:text-base">
                      Viele Vertriebsorganisationen arbeiten hart, aber nicht an den richtigen Stellen.
                      Unsere Sales Engine macht sichtbar, welche Hebel zuerst bewegt werden müssen,
                      um planbarer zu wachsen.
                    </p>
                    <div className="space-y-3 text-primary-foreground/90 text-sm md:text-base">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                        <span>Klare Prioritäten statt operativer Unschärfe</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                        <span>Messbare Hebel statt allgemeiner Empfehlungen</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                        <span>Strukturierte Umsetzung statt reiner Analyse</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── FÜR WEN IST DAS? ── */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center mb-12"
            >
              <Badge className="mb-4">Zielgruppe</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Für wen ist das richtig?
              </h2>
              <p className="text-lg text-muted-foreground">
                Unser Ansatz ist nicht für jeden. Er ist für Unternehmen, die mit Substanz wachsen wollen.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {targetGroups.map((group, index) => (
                <motion.div
                  key={group.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  <Card className="h-full border-border/60 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                        <group.icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-base font-semibold mb-2 leading-snug">{group.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {group.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINALER CTA ── */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <Card className="border-primary/20 shadow-2xl">
                <CardContent className="p-8 md:p-12 text-center">
                  <Badge className="mb-5">Nächster Schritt</Badge>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    Erst erkennen, dann gezielt verbessern
                  </h2>
                  <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
                    Starten Sie mit dem Audit und machen Sie sichtbar, wo Ihr Vertrieb heute Umsatz
                    verliert. Wenn sinnvoll, entwickeln wir daraus gemeinsam die passende Sales Engine.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button asChild size="lg" className="text-lg px-8 py-6 h-auto">
                      <Link to={ROUTE_PATHS.AUDIT}>
                        Audit starten
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Link>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 h-auto">
                      <Link to={ROUTE_PATHS.KONTAKT}>Kontakt aufnehmen</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

      </div>
    </Layout>
  );
}
